/**
 * HOW A FAILED READ IS SHOWN — ONE COMPONENT, BECAUSE THE FIVE FAILURES ARE
 * FIVE DIFFERENT FACTS AND MUST NOT RENDER ALIKE.
 *
 * `src/web/gateway.ts` classifies a failure by STATUS and by NAME, and this is
 * where that classification becomes words:
 *
 *   no session → not signed in. Signing in happens at the identity provider;
 *          this workspace holds no Scout credential, so there is nothing to do
 *          here.
 *   not Owner  → a session was verified and it is somebody else's. SAYING SO
 *          MATTERS, because the obvious next action — sign in again — will not
 *          change it, and a screen that let the Owner retry for ever would be
 *          hiding the one fact that resolves it.
 *   deployment → Scout ANSWERED, in its own envelope, to say it is deliberately
 *          not serving: a schema behind the code, a hold armed until a release
 *          is authorized. NOTHING THE READER DOES changes it, so there is NO
 *          RETRY — a retry here is an invitation to hammer a deployment that is
 *          behaving exactly as intended, which is the confusion `session.ts` was
 *          written to prevent and which this panel used to reintroduce one level
 *          down (Phase 6A §16.3).
 *   refused    → Scout answered and declined, for a reason about the request
 *          rather than the person. Also no retry, and deliberately NOT dressed
 *          as an identity verdict.
 *   else       → Scout did not answer, with the name, the status and a retry.
 *
 * IT WAS EXTRACTED FROM THE STATUS VIEW RATHER THAN COPIED. Four more views need
 * exactly this, and four copies is four chances for the 403 to drift into a
 * generic "something went wrong" — which is the failure mode that matters,
 * because the generic version tells the Owner to try again when trying again
 * cannot work.
 */

import { isAuthenticationFailure, isNotTheOwner, type GatewayError } from '../gateway';
import { isDeploymentState, isRefusedRequest } from '../session';

export function Failure({
	error,
	onRetry,
	what,
}: {
	error: GatewayError;
	onRetry: () => void;
	/** What could not be read, in the view's own words — "the inbox", "your
	 *  conversations". Named so the sentence is about a thing rather than about
	 *  the mechanism. */
	what: string;
}) {
	if (isAuthenticationFailure(error)) {
		return (
			<div class="panel failure" role="alert">
				<h2>Not signed in to Scout</h2>
				<p>
					Scout did not accept this session, so {what} could not be read. Signing in again is
					done at the identity provider, not here — this workspace never holds a Scout
					credential of its own.
				</p>
				<p class="muted small">
					Scout’s own words: <code>{error.name}</code> — {error.message}
				</p>
			</div>
		);
	}
	if (isNotTheOwner(error)) {
		return (
			<div class="panel failure" role="alert">
				<h2>This account is not Scout’s Owner</h2>
				<p>
					The identity was verified and it is not the one this deployment answers to, so{' '}
					{what} is not readable from this session. Signing in again will not change that.
				</p>
				<p class="muted small">
					Scout’s own words: <code>{error.name}</code> — {error.message}
				</p>
			</div>
		);
	}
	if (isDeploymentState(error)) {
		// NO RETRY, AND THE ABSENCE IS THE POINT. Scout answered this request; it
		// said, by name, that it is not serving — and the two states that produce
		// this are a database behind the code and a release the Owner has not yet
		// authorized. A "Read again" button under that sentence would be the page
		// telling its reader to do the one thing that cannot help.
		return (
			<div class="panel failure" role="alert">
				<h2>Scout is not serving right now</h2>
				<p>
					Scout answered {what} to say that it is deliberately not serving — a deployment state,
					not a problem with your session and not a network fault. Nothing you can do here changes
					it, so there is no retry on this panel.
				</p>
				<p class="muted small">
					Scout’s own words: <code>{error.name}</code> — {error.message}
				</p>
			</div>
		);
	}
	if (isRefusedRequest(error)) {
		return (
			<div class="panel failure" role="alert">
				<h2>Scout refused this request</h2>
				<p>
					Scout answered and declined, and the reason is about the request rather than about who
					you are — so it is not shown as a session problem, and reading again will not change
					it.
				</p>
				<p class="muted small">
					Scout’s own words: <code>{error.name}</code> — {error.message}
				</p>
			</div>
		);
	}
	return (
		<div class="panel failure" role="alert">
			<h2>Scout did not answer</h2>
			<p>{error.message}</p>
			<p class="muted small">
				<code>{error.name}</code>
				{error.status > 0 && ` · HTTP ${error.status}`}
			</p>
			<button type="button" class="button" onClick={onRetry}>
				Read again
			</button>
		</div>
	);
}
